import { CheckIcon } from '@heroicons/react/20/solid'
import { useTheme } from '../../context/ThemeContext';
import React from 'react';

const tiers = [
  {
    name: 'Hobby',
    id: 'tier-hobby',
    href: '#/login',
    priceMonthly: '₹0',
    description: "The perfect plan if you're just getting started with our product.",
    features: [
      '25 products',
      'Up to 10,000 subscribers', 
      'Advanced analytics', 
      '24-hour support response time',
      'Marketing automations',
      'Custom integrations',
    ],
    featured: true,
  },
  {
    name: 'Professional',
    id: 'tier-professional',
    href: '#/login',
    priceMonthly: '₹99',
    description: 'For professionals who need more advanced features and support.',
    features: [
      'Unlimited products',
      'Unlimited subscribers',
      'Advanced analytics',
      'Dedicated support representative',
      'Marketing automations',
      'Custom integrations',
    ],
    featured: false,
  },
  {
    name: 'Enterprise',
    id: 'tier-enterprise',
    href: '#/login',
    priceMonthly: '₹199',
    description: 'Dedicated support and infrastructure for your company.',
    features: [
      'Unlimited products',
      'Unlimited subscribers',
      'Advanced analytics',
      'Dedicated support representative',
      'Marketing automations',
      'Custom integrations',
    ],
    featured: false,
  },
]

function classNames(...classes:any) {
  return classes.filter(Boolean).join(' ')
}

export const PricingPage : React.FC = () => {
  const { isDarkMode } = useTheme();
  const [featuredIndex, setFeaturedIndex] = React.useState(1);

  return (
    <div className={`relative isolate px-6 py-24 sm:py-12 lg:px-8 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
      <div aria-hidden="true" className="absolute inset-x-0 -top-3 -z-10 transform-gpu overflow-hidden px-36 blur-3xl">
        <div
          style={{
            clipPath:
              'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
          }}
          className={`mx-auto aspect-[1155/678] w-[72.1875rem] bg-gradient-to-tr from-accent-primary to-accent-secondary opacity-30`}
        />
      </div>
      <div className="mx-auto max-w-4xl text-center">
        <h2 className={`text-base/7 font-semibold ${isDarkMode ? 'text-accent-primary' : 'text-accent-primary'}`}>Pricing</h2>
        <p className={`mt-2 text-balance text-5xl font-semibold tracking-tight sm:text-6xl ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Choose the right plan for you
        </p>
      </div>
      <p className={`mx-auto mt-6 max-w-2xl text-pretty text-center text-lg font-medium sm:text-xl/8 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        Choose an affordable plan that's packed with the best features for engaging your audience, creating customer
        loyalty, and driving sales.
      </p>
      <div className="mx-auto mt-16 grid max-w-lg grid-cols-1 items-center gap-x-2 gap-y-6 sm:mt-20 sm:gap-y-0 lg:max-w-4xl lg:grid-cols-3">
        {tiers.map((tier, tierIdx) => {
            const isFeatured = tierIdx === featuredIndex;

        return (
          <div  onClick={() => setFeaturedIndex(tierIdx)}
            key={tier.id}
            className={classNames(
                isFeatured ? `relative ${isDarkMode ? 'bg-gray-800' : 'bg-gray-900'} shadow-2xl` : `${isDarkMode ? 'bg-gray-900/60' : 'bg-white/60'} sm:mx-8 lg:mx-0`,
                isFeatured
                    ? ''
                    : tierIdx === 0
                      ? 'rounded-t-3xl sm:rounded-b-none lg:rounded-bl-3xl lg:rounded-tr-none'
                      : 'sm:rounded-t-none lg:rounded-bl-none lg:rounded-tr-3xl',
                `rounded-3xl p-8 ring-1 ${isDarkMode ? 'ring-gray-700' : 'ring-gray-900/10'} sm:p-10`,
            )}
          >
            <h3
              id={tier.id}
              className={classNames(isFeatured ? 'text-accent-primary' : 'text-accent-primary', 'text-base/7 font-semibold')}
            >
              {tier.name}
            </h3>
            <p className="mt-4 flex items-baseline gap-x-2">
              <span
                className={classNames(
                    isFeatured ? 'text-white' : isDarkMode ? 'text-white' : 'text-gray-900',
                    'text-5xl font-semibold tracking-tight',
                )}
              >
                {tier.priceMonthly}
              </span>
              <span className={classNames(isFeatured ? 'text-gray-400' : isDarkMode ? 'text-gray-400' : 'text-gray-500', 'text-base')}>/month</span>
            </p>
            <p className={classNames(isFeatured ? 'text-gray-300' : isDarkMode ? 'text-gray-300' : 'text-gray-600', 'mt-6 text-base/7')}>
              {tier.description}
            </p>
            <ul
              role="list"
              className={classNames(
                isFeatured ? 'text-gray-300' : isDarkMode ? 'text-gray-300' : 'text-gray-600',
                'mt-8 space-y-3 text-sm/6 sm:mt-10',
              )}
            >
              {tier.features.map((feature) => (
                <li key={feature} className="flex gap-x-3">
                  <CheckIcon
                    aria-hidden="true"
                    className={classNames(isFeatured ? 'text-accent-primary' : 'text-accent-primary', 'h-6 w-5 flex-none')}
                  />
                  {feature}
                </li>
              ))}
            </ul>
            <a
              href={tier.href}
              aria-describedby={tier.id}
              className={classNames(
                isFeatured
                  ? 'bg-accent-primary text-white shadow-sm hover:bg-accent-primary-dark focus-visible:outline-accent-primary'
                  : 'text-accent-primary ring-1 ring-inset ring-accent-primary/20 hover:ring-accent-primary/30 focus-visible:outline-accent-primary',
                'mt-8 block rounded-md px-3.5 py-2.5 text-center text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 sm:mt-10',
              )}
            >
              Get started today
            </a>
          </div>
        )
    }
    )}
      </div>
    </div>
  )
}
